import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaArrowTrendUp, FaArrowTrendDown } from "react-icons/fa6";
import { IoShieldCheckmarkOutline } from "react-icons/io5";

export default function BottomCards() {
  const { t } = useTranslation();

  const cards = [
    {
      id: 1,
      icon: FaArrowTrendDown,
      gradient: "to-[#F7D6DB] via-[#E78E98] from-[#D96579]",
      key: "decline",
      color: "rose",
      iconColor: "text-rose-600",
      titleColor: "text-rose-800",
      borderColor: "border-rose-200",
      shadowColor: "shadow-rose-200/50",
      clipPath: "polygon(85% 0, 100% 50%, 85% 100%, 0% 100%, 7% 54%, 0% 0%)",
    },
    {
      id: 2,
      icon: IoShieldCheckmarkOutline,
      gradient: "to-[#FFF5D6] via-[#FFE6A6] from-[#FFD26D]",
      key: "stability",
      color: "amber",
      iconColor: "text-amber-600",
      titleColor: "text-amber-800",
      borderColor: "border-amber-200",
      shadowColor: "shadow-amber-200/50",
      clipPath: "polygon(85% 0, 100% 50%, 85% 100%, 0% 100%, 7% 54%, 0% 0%)",
    },
    {
      id: 3,
      icon: FaArrowTrendUp,
      gradient: "to-[#C4ECE5] via-[#8FD8E6] from-[#3FA9DD]",
      key: "growth",
      color: "cyan",
      iconColor: "text-cyan-600",
      titleColor: "text-cyan-800",
      borderColor: "border-cyan-200",
      shadowColor: "shadow-cyan-200/50",
      clipPath: "polygon(85% 0, 100% 50%, 85% 100%, 0% 100%, 7% 54%, 0% 0%)",
    },
  ];

  return (
    <div className="mt-6 grid grid-cols-1 gap-2 p-2 md:grid-cols-3">
      {cards.map((card, index) => {
        const Icon = card.icon;

        return (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            whileHover={{
              y: -8,
              scale: 1.03,
              transition: { duration: 0.2 },
            }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            className="group relative"
            style={{
              clipPath: card.clipPath,
            }}
          >
            <div
              className={`
                absolute
                -inset-1
                rounded-3xl
                bg-gradient-to-r
                ${card.gradient}
                opacity-0
                blur-xl
                transition-opacity
                duration-300
                group-hover:opacity-70
                ${card.shadowColor}
              `}
            />

            <div
              className={`
                relative
                flex
                h-[130px]
                items-center
                justify-center
                gap-3
                bg-gradient-to-r
                p-4
                ${card.gradient}
                border
                ${card.borderColor}
                border-opacity-30
                shadow-lg
                transition-all
                duration-300
                group-hover:shadow-xl
              `}
            >
              <div className="absolute left-3 top-3 flex gap-1">
                <span className="h-3 w-3 rounded-full bg-white/20" />
                <span className="h-3 w-3 rounded-full bg-white/10" />
                <span className="h-3 w-3 rounded-full bg-white/5" />
              </div>

              <div className="flex flex-col justify-center">
                <span className="mb-1 text-xs font-medium text-white/40">
                  {String(card.id).padStart(2, "0")}
                </span>

                <h3
                  className={`
                    text-center
                    font-extrabold
                    tracking-tight
                    ${card.titleColor}
                    transition-colors
                    duration-300
                    group-hover:scale-105
                  `}
                >
                  {t(`assessment.industryLifeCycle.${card.key}`)}
                </h3>

                <div className="mx-auto my-1.5 h-0.5 w-8 rounded-full bg-white/30" />

                <p className="max-w-[130px] text-center text-xs font-bold leading-relaxed text-gray-900 md:max-w-[180px]">
                  {t(`assessment.industryLifeCycle.${card.key}Desc`)}
                </p>
              </div>

              <div
                className={`
                  relative
                  flex
                  shrink-0
                  items-center
                  justify-center
                  rounded-full
                  bg-white/90
                  p-4
                  shadow-md
                  backdrop-blur-sm
                  transition-all
                  duration-300
                  group-hover:rotate-12
                  group-hover:scale-110
                  group-hover:shadow-lg
                `}
              >
                <div
                  className={`absolute inset-0 animate-pulse rounded-full border-2 ${card.borderColor}`}
                />
                <Icon size={30} className={card.iconColor} />
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
